import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

/**
 * Development Logger Service
 * 
 * A configurable logging service for development debugging.
 * Enable/disable via environment.devLogging settings.
 * 
 * Usage:
 *   constructor(private devLogger: DevLoggerService) {}
 *   
 *   this.devLogger.log('MyComponent', 'Something happened', data);
 *   this.devLogger.warn('MyService', 'Warning message');
 *   this.devLogger.error('MyService', 'Error occurred', error);
 *   this.devLogger.api('GET /planning', response);
 *   this.devLogger.conflict('Conflict detected', conflictData);
 * 
 * All logs are prefixed with [PT4-DEV] for easy filtering in browser console.
 * To find and remove: search for "DevLoggerService" or "[PT4-DEV]"
 */

export interface DevLoggingConfig {
  enabled: boolean;
  logApi?: boolean;        // Log API calls
  logConflicts?: boolean;  // Log conflict calculations
  logPlanning?: boolean;   // Log planning actions (assign, unplan, etc.)
  logState?: boolean;      // Log state changes
  logVerbose?: boolean;    // Extra verbose logging
}

@Injectable({
  providedIn: 'root'
})
export class DevLoggerService {
  private readonly PREFIX = '[PT4-DEV]';
  private config: DevLoggingConfig;

  constructor() {
    // Get config from environment, with defaults
    this.config = (environment as any).devLogging || { enabled: false };
  }

  /** Check if logging is enabled */
  get isEnabled(): boolean {
    return this.config.enabled && !environment.production;
  }

  /** General log */
  log(category: string, message: string, ...data: any[]): void {
    if (!this.isEnabled) return;
    console.log(`${this.PREFIX} [${category}]`, message, ...data);
  }

  /** Warning log */
  warn(category: string, message: string, ...data: any[]): void {
    if (!this.isEnabled) return;
    console.warn(`${this.PREFIX} [${category}]`, message, ...data);
  }

  /** Error log */
  error(category: string, message: string, ...data: any[]): void {
    if (!this.isEnabled) return;
    console.error(`${this.PREFIX} [${category}]`, message, ...data);
  }

  /** API call logging */
  api(endpoint: string, data?: any, isError: boolean = false): void {
    if (!this.isEnabled || !this.config.logApi) return;
    if (isError) {
      console.error(`${this.PREFIX} [API ERROR]`, endpoint, data);
    } else {
      console.log(`${this.PREFIX} [API]`, endpoint, data);
    }
  }

  /** API request start */
  apiRequest(method: string, url: string, body?: any): void {
    if (!this.isEnabled || !this.config.logApi) return;
    console.log(`${this.PREFIX} [API →]`, `${method} ${url}`, body || '');
  }

  /** API response */
  apiResponse(method: string, url: string, response: any, durationMs?: number): void {
    if (!this.isEnabled || !this.config.logApi) return;
    const duration = durationMs ? `(${durationMs}ms)` : '';
    console.log(`${this.PREFIX} [API ←]`, `${method} ${url} ${duration}`, response);
  }

  /** Conflict logging */
  conflict(message: string, data?: any): void {
    if (!this.isEnabled || !this.config.logConflicts) return;
    console.log(`${this.PREFIX} [CONFLICT]`, message, data || '');
  }

  /** Planning action logging */
  planning(action: string, data?: any): void {
    if (!this.isEnabled || !this.config.logPlanning) return;
    console.log(`${this.PREFIX} [PLANNING]`, action, data || '');
  }

  /** State change logging */
  state(component: string, stateName: string, value: any): void {
    if (!this.isEnabled || !this.config.logState) return;
    console.log(`${this.PREFIX} [STATE]`, `${component}.${stateName} =`, value);
  }

  /** Verbose/debug logging */
  verbose(category: string, message: string, ...data: any[]): void {
    if (!this.isEnabled || !this.config.logVerbose) return;
    console.debug(`${this.PREFIX} [VERBOSE] [${category}]`, message, ...data);
  }

  /** Group logs together (collapsible in console) */
  group(label: string): void {
    if (!this.isEnabled) return;
    console.groupCollapsed(`${this.PREFIX} ${label}`);
  }

  /** End grouped logs */
  groupEnd(): void {
    if (!this.isEnabled) return;
    console.groupEnd();
  }

  /** Table display for arrays/objects */
  table(category: string, data: any): void {
    if (!this.isEnabled) return;
    console.log(`${this.PREFIX} [${category}]`);
    console.table(data);
  }

  /** Time tracking - start */
  timeStart(label: string): void {
    if (!this.isEnabled) return;
    console.time(`${this.PREFIX} ${label}`);
  }

  /** Time tracking - end */
  timeEnd(label: string): void {
    if (!this.isEnabled) return;
    console.timeEnd(`${this.PREFIX} ${label}`);
  }
}
